import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { loginWithDevToken } from '@/devAuth';
import { RECUBE_LOGIN_URL, clearReconnectIntent, reconnectPending } from '@/services/session';
import styles from './LoginPage.module.scss';

const RECUBE_ERRORS: Record<string, string> = {
  token_exchange: "Échec de l'échange du code Recube.",
  userinfo: 'Impossible de lire ton profil Recube.',
  profil_incomplet: 'Profil Recube incomplet (pseudo manquant).',
  state_invalide: 'Session de connexion expirée, réessaie.',
  no_access_token: 'Recube n\'a pas renvoyé de token.',
  exception: 'Erreur pendant la connexion Recube.',
  missing_code: 'Code d\'autorisation manquant.',
  // Recube limite /oauth/token à 10 échanges par minute et par IP. L'échange étant fait par
  // l'API, tout le staff partage la même IP : plusieurs connexions simultanées suffisent.
  trop_de_connexions: 'Trop de connexions en même temps côté Recube, réessaie dans une minute.',
  // Le scope « profile:read » est optionnel : si le client OAuth n'en est pas provisionné,
  // Recube renvoie invalid_scope avant même l'écran de consentement.
  invalid_scope: "Recube refuse les permissions demandées par le panel (scope non autorisé pour ce client).",
  access_denied: 'Connexion refusée côté Recube.',
};

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // check-access en échec côté serveur (api_error : 503, MongoDB en panne...) : on propose l'accès
  // de secours au Déploiement, qui ne dépend que du CICD.
  const [panelUnavailable, setPanelUnavailable] = useState(false);
  // Base des grades en panne (503 GRADE_BACKEND_UNAVAILABLE seulement) : identifiant de compte à
  // ajouter au secret PANEL_EMERGENCY_ADMINS pour l'accès d'urgence au panel. Vient du corps du 503
  // (ou du jeton), jamais d'un appel qui dépend de Mongo. Tout compte Recube le voit pendant la
  // panne : le bloc rappelle qu'il est réservé aux admins et qu'on n'ajoute que son propre identifiant.
  const [emergencyId, setEmergencyId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [devToken, setDevToken] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // Reconnexion ouverte depuis un onglet dont la session a expiré : une fois connecté, on renvoie
  // vers cet onglet (son travail y est resté) au lieu de partir sur l'accueil.
  const [reconnecting] = useState(() => reconnectPending() || new URLSearchParams(window.location.search).has('reconnexion'));
  const [renewed, setRenewed] = useState(false);
  // Retour de Recube avec un jeton : pas de départ vers l'accueil avant de l'avoir enregistré.
  const [hashToken] = useState(() => new URLSearchParams(window.location.hash.slice(1)).has('token'));

  const finishLogin = () => {
    if (!reconnecting) {
      navigate('/', { replace: true });
      return;
    }
    clearReconnectIntent();
    setRenewed(true);
    setIsLoading(false);
    setDevLoading(false);
    // Fermeture permise seulement si l'onglet n'a pas navigué (connexion locale) : sinon le message reste.
    window.close();
  };

  // Retour du backend après login Recube : #token=... (succès) ou #error=... (échec).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const p = new URLSearchParams(hash.slice(1));
    const token = p.get('token');
    const err = p.get('error');
    // Nettoie le fragment (ne pas laisser le token dans l'URL).
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (err) {
      setError(RECUBE_ERRORS[err] ?? `Connexion Recube échouée (${err}).`);
      return;
    }
    if (token) {
      setIsLoading(true);
      loginWithDevToken(token).then((r) => {
        if (r.ok) finishLogin();
        else {
          setError(r.error ?? 'Échec de la connexion Recube.');
          setPanelUnavailable(r.reason === 'api_error');
          setEmergencyId(r.reason === 'api_error' ? r.emergencyId ?? null : null);
          setIsLoading(false);
        }
      });
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Déjà connecté → accueil (Dashboard, ou première page servie par le groupe de jeu). Pas pendant
  // une reconnexion : l'état « connecté » gardé par le navigateur est celui de la session expirée.
  useEffect(() => {
    if (isAuthenticated && !reconnecting && !hashToken) navigate('/', { replace: true });
  }, [isAuthenticated, navigate, reconnecting, hashToken]);

  const handleDevLogin = async () => {
    setDevLoading(true);
    setError(null);
    const result = await loginWithDevToken(devToken);
    if (result.ok) {
      finishLogin();
    } else {
      setError(result.error ?? 'Échec de la connexion dev.');
      setPanelUnavailable(result.reason === 'api_error');
      setEmergencyId(result.reason === 'api_error' ? result.emergencyId ?? null : null);
      setDevLoading(false);
    }
  };

  const handleRecubeLogin = () => {
    setIsLoading(true);
    setError(null);
    setPanelUnavailable(false);
    setEmergencyId(null);
    // Redirige vers le backend qui lance le flux OAuth Recube (PKCE côté serveur).
    window.location.href = RECUBE_LOGIN_URL;
  };

  const copyEmergencyId = async () => {
    if (!emergencyId) return;
    try {
      await navigator.clipboard.writeText(emergencyId);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : on sélectionne le texte pour
      // un Ctrl+C manuel.
      const el = document.getElementById('emergency-id');
      if (el) window.getSelection()?.selectAllChildren(el);
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <img src="/recube-logo.png" alt="Recube" className={styles.logo} />
          <h1 className={styles.title}>Staff Panel</h1>
          <p className={styles.subtitle}>
            Connectez-vous avec votre compte Recube pour accéder au panel d'administration.
          </p>
        </div>

        {renewed && (
          <div className={styles.renewed} role="status">
            <strong>Session renouvelée.</strong>
            <p>Tu peux fermer cet onglet et revenir à ton travail : il t'attend tel que tu l'as laissé.</p>
            <button type="button" onClick={() => navigate('/', { replace: true })}>Continuer dans cet onglet</button>
          </div>
        )}

        {reconnecting && !renewed && (
          <div className={styles.reconnect} role="status">
            Ta session a expiré. Reconnecte-toi pour reprendre : ton travail t'attend dans l'autre onglet,
            tes modifications sont gardées.
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}

        {panelUnavailable && (
          <div className={styles.emergency}>
            <p>
              Le panel ne peut pas vérifier ton accès pour le moment. La section Déploiement reste
              joignable avec tes identifiants CICD.
            </p>
            {/* Lien plein : /secours est servi par un routeur à part (cf. App). */}
            <a href="/secours" className={styles.emergencyLink}>Accès de secours au Déploiement</a>
            {emergencyId && (
              <div className={styles.emergencyId}>
                <p>
                  <strong>Accès d'urgence au panel, réservé aux administrateurs.</strong> Si tu n'en es pas
                  un, ignore ce bloc : personne ne doit te demander cet identifiant.
                </p>
                <p>Ton identifiant de compte :</p>
                <div className={styles.emergencyIdRow}>
                  <code id="emergency-id">{emergencyId}</code>
                  <button type="button" onClick={() => void copyEmergencyId()}>
                    {copied ? 'Copié' : 'Copier'}
                  </button>
                </div>
                <p>
                  Ajoute cet identifiant au secret GitHub <strong>PANEL_EMERGENCY_ADMINS</strong> du dépôt
                  BE-Panel puis relance le déploiement.
                </p>
                <p>
                  N'y ajoute jamais un identifiant reçu d'un tiers, seulement le tien, copié ici : il donne
                  les droits admin complets pendant une panne.
                </p>
              </div>
            )}
          </div>
        )}

        {!renewed && <button className={styles.xboxButton} onClick={handleRecubeLogin} disabled={isLoading}>
          {isLoading ? (
            <span className={styles.loader}>
              <span />
              <span />
              <span />
            </span>
          ) : (
            <span>Se connecter avec Recube</span>
          )}
        </button>}

        <p className={styles.footer}>
          Seuls les membres du staff autorisés peuvent accéder à ce panel.
        </p>

        {import.meta.env.DEV && !renewed && (
          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px dashed rgba(255,255,255,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <label style={{ fontSize: 12, opacity: 0.7 }}>
              🛠️ Connexion locale (dev) — collez un token JWT valide (clé <code>recubebe_xbox_token</code>) :
            </label>
            <textarea
              value={devToken}
              onChange={(e) => setDevToken(e.target.value)}
              placeholder="eyJhbGciOi..."
              rows={3}
              spellCheck={false}
              style={{
                width: '100%',
                fontFamily: 'monospace',
                fontSize: 12,
                padding: 8,
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(0,0,0,0.3)',
                color: 'inherit',
                resize: 'vertical',
              }}
            />
            <button
              type="button"
              onClick={handleDevLogin}
              disabled={devLoading || !devToken.trim()}
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                cursor: devLoading || !devToken.trim() ? 'not-allowed' : 'pointer',
                opacity: devLoading || !devToken.trim() ? 0.5 : 1,
              }}
            >
              {devLoading ? 'Connexion...' : 'Se connecter (dev)'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
